/**
 * Puerto Rico アクション選択バリデータ
 * @see docs/specifications/logic/puerto_rico/phase_03_select_actions_override.md
 */

import type { PuertoRicoGameState, PuertoRicoActionType } from '../../types';

/** バリデーション結果 */
export interface ValidationResult {
  valid: boolean;
  errorCode?: string;
  errorMessage?: string;
}

/** アクション選択コスト */
const ACTION_SELECTION_COST = 5;

/** 利用可能なアクション */
const VALID_ACTIONS: PuertoRicoActionType[] = ['ENGINEER', 'LOCOMOTIVE'];

/** 機関車レベル上限 */
const MAX_LOCOMOTIVE_LEVEL = 6;

/**
 * アクション選択のバリデーション
 *
 * @param G - ゲーム状態
 * @param playerId - プレイヤーID
 * @param action - 選択するアクション
 * @returns バリデーション結果
 */
export function validateSelectAction(
  G: PuertoRicoGameState,
  playerId: string,
  action: PuertoRicoActionType
): ValidationResult {
  const player = G.players[playerId];

  if (!player) {
    return {
      valid: false,
      errorCode: 'PLAYER_NOT_FOUND',
      errorMessage: 'プレイヤーが見つかりません'
    };
  }

  // 1. アクション種別チェック
  if (!VALID_ACTIONS.includes(action)) {
    return {
      valid: false,
      errorCode: 'INVALID_ACTION_PUERTO_RICO',
      errorMessage: 'Puerto Ricoでは使用できないアクションです'
    };
  }

  // 2. 既にアクション選択済みかチェック
  if (G.isActionSelectionComplete) {
    return {
      valid: false,
      errorCode: 'ACTION_ALREADY_SELECTED',
      errorMessage: 'このターンは既にアクション選択が完了しています'
    };
  }

  // 3. コスト支払いチェック
  if (player.cash < ACTION_SELECTION_COST) {
    return {
      valid: false,
      errorCode: 'INSUFFICIENT_FUNDS',
      errorMessage: `アクション選択には$${ACTION_SELECTION_COST}が必要です`
    };
  }

  // 4. Locomotive 固有のバリデーション
  if (action === 'LOCOMOTIVE') {
    if (player.locomotiveLevel >= MAX_LOCOMOTIVE_LEVEL) {
      return {
        valid: false,
        errorCode: 'MAX_LOCOMOTIVE_LEVEL',
        errorMessage: `機関車レベルは最大${MAX_LOCOMOTIVE_LEVEL}リンクです`
      };
    }
  }

  return { valid: true };
}

/**
 * アクション選択スキップのバリデーション
 *
 * @param G - ゲーム状態
 * @param playerId - プレイヤーID
 * @returns バリデーション結果
 */
export function validateSkipAction(
  G: PuertoRicoGameState,
  playerId: string
): ValidationResult {
  const player = G.players[playerId];

  if (!player) {
    return {
      valid: false,
      errorCode: 'PLAYER_NOT_FOUND',
      errorMessage: 'プレイヤーが見つかりません'
    };
  }

  // 既にアクション選択済みかチェック
  if (G.isActionSelectionComplete) {
    return {
      valid: false,
      errorCode: 'ACTION_ALREADY_SELECTED',
      errorMessage: 'このターンは既にアクション選択が完了しています'
    };
  }

  return { valid: true };
}

/**
 * アクション選択可能かどうかを判定
 * （資金が十分にあるか）
 *
 * @param G - ゲーム状態
 * @param playerId - プレイヤーID
 * @returns アクション選択可能なら true
 */
export function canSelectAction(
  G: PuertoRicoGameState,
  playerId: string
): boolean {
  const player = G.players[playerId];
  if (!player) return false;
  if (G.isActionSelectionComplete) return false;
  return player.cash >= ACTION_SELECTION_COST;
}
